import { createRandomUUID } from "../helpers/Utilities";
import { APIMRequest, APIMResponse, isTokenExpired } from "./APIMRequest";
import { LoginManager } from "./LoginManager";
import { ServerInfo } from "./ServerInfo";
import { ErrorLogger, ErrorSeverity } from "./errorLogger.service";

export class AttachmentPutRequest extends APIMRequest {
  file: File;

  constructor(file: File) {
    super();
    this.file = file;
    this.method = "PUT";
  }

  async send(): Promise<APIMResponse> {
    try {
      const loginManager = new LoginManager();
      const environment = await ServerInfo.get();
      
      let access_token = await loginManager.getAccessToken(environment.oidcSettings.scope);
      
      // Check if token is expired or about to expire
      if (isTokenExpired(access_token)) {
        await loginManager.refreshIdToken(environment.oidcSettings.scope);
        access_token = await loginManager.getAccessToken(environment.oidcSettings.scope);
      }
      
      return await this.makePutRequestAsync(access_token, environment);
    } catch (error: any) {
      console.error('Failed to send attachment:', error);
      ErrorLogger.get().logError(error, {
        component: 'AttachmentPutRequest',
        action: 'send',
        severity: ErrorSeverity.Error,
        additionalData: {
          fileName: this.file.name,
          fileSize: this.file.size
        }
      });
      const statusCode = error.statusCode || 500;
      const message = error.message || String(error);
      const err = new Error(message);
      Object.assign(err, { 
        statusCode: "N", 
        messageCode: statusCode, 
        statusMessage: message, 
        developerMessage: message 
      });
      throw err;
    }
  }

  private async makePutRequestAsync(access_token: string, environment: any): Promise<APIMResponse> {
    return new Promise((resolve, reject) => {
      this.makePutRequest(access_token, environment, resolve, reject);
    });
  }

  private makePutRequest(
    access_token: string,
    environment: any,
    resolve: (value: APIMResponse) => void,
    reject: (reason: any) => void
  ): void {
    const xhr = new XMLHttpRequest();
    const gid = createRandomUUID().replaceAll("-", "");
    const nameWithoutSpaces = this.file.name.replaceAll(" ", "");
    const lastDot = nameWithoutSpaces.lastIndexOf(".");
    const stem = lastDot > 0 ? nameWithoutSpaces.substring(0, lastDot) : nameWithoutSpaces;
    const ext = lastDot > 0 ? nameWithoutSpaces.substring(lastDot) : "";
    const encodedStem = btoa(
      Array.from(new TextEncoder().encode(stem), b => String.fromCodePoint(b)).join("")
    ).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    const fnm = encodedStem + ext;
    const url = environment.attachmentUrl + gid.substring(0, 8) + '-' + fnm;
    const mim = 'application/pdf';
    const bin = new Blob([this.file], {type: mim});
    xhr.open(this.method, url);
    if (environment.apimSubscriptionKey) {
      xhr.setRequestHeader("Ocp-Apim-Subscription-Key", environment.apimSubscriptionKey);
    }
    xhr.setRequestHeader("Authorization", "Bearer " + access_token);

    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.send(bin);
    xhr.onreadystatechange = (e) => {
      if (xhr.readyState === 4) {
        if (xhr.status === 201) {
          // Store only the document name (not the full URL) for environment-agnostic resolution
          const documentName = gid.substring(0, 8) + '-' + fnm;
          const response: APIMResponse = { statusCode: "Y", messageCode: xhr.status, statusMessage: "File uploaded.", result: documentName, developerMessage:"" };
          resolve(response);
        } else {
          const message = `Upload failed with a status of ${xhr.status}.`
          resolve({ statusCode: "N", messageCode: xhr.status, statusMessage: message, developerMessage:"" })
        }
      }
    };
  }
}
